import type { ApiErrorBody, AuthResult } from '@trafiku/contracts';
import { translateServerMessage } from './i18n';

const BASE = '/api/admin/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string>,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<AuthResult | null> | null = null;
let onSessionLost: () => void = () => {};

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function onUnauthorized(handler: () => void) {
  onSessionLost = handler;
}

/** Exchanges the httpOnly refresh cookie for a new access token. Concurrent callers share one request. */
export function refreshSession(): Promise<AuthResult | null> {
  refreshing ??= fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'same-origin' })
    .then(async (res) => {
      if (!res.ok) return null;
      const result = (await res.json()) as AuthResult;
      accessToken = result.accessToken;
      return result;
    })
    .catch(() => null)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

type Body = unknown;
interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: Body;
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function api<T = unknown>(path: string, opts: Options = {}, retried = false): Promise<T> {
  const qs = opts.query
    ? `?${new URLSearchParams(
        Object.entries(opts.query)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      )}`
    : '';
  const headers: Record<string, string> = {};
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(`${BASE}${path}${qs}`, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'same-origin',
    body: opts.form ?? (opts.body === undefined ? undefined : JSON.stringify(opts.body)),
  });

  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    if (await refreshSession()) return api<T>(path, opts, true);
    onSessionLost();
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as ApiErrorBody).error;
    // Messages come from the API in English; show them in the UI language.
    const fields = err?.fields
      ? Object.fromEntries(Object.entries(err.fields).map(([k, v]) => [k, translateServerMessage(v)]))
      : undefined;
    throw new ApiError(
      res.status,
      err?.code ?? 'error',
      translateServerMessage(err?.message ?? res.statusText),
      fields,
    );
  }
  return data as T;
}

export async function uploadImage(kind: string, file: File): Promise<string> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  const { url } = await api<{ url: string }>('/uploads', { method: 'POST', form });
  return url;
}
